import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { PaginationDirection } from '../../__generated__/globalTypes';
import { WorkItemFragment } from '../../components/alerts/workItemFragment';
import { collectAlertCardIds } from '../../lib/assistant/alertCards';
import { isWorkItemUnread, workItemToListedAlert } from '../../utils/alert/workItemAlertRow';

import type {
  GetAlertCardWorkItemsQuery,
  GetAlertCardWorkItemsQueryVariables,
} from './__generated__/useAlertCardWorkItems.gql';
import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { AlertRowFields } from '../../utils/alert/alertRow';

/** What a transcript's cards need: the row to render, and whether it is new. */
export interface IAlertCardWorkItems {
  cards: Record<string, AlertRowFields>;
  /** Alerts already read, waiting on someone, or archived. */
  workedAlertIds: ReadonlySet<string>;
}

const GET_ALERT_CARD_WORK_ITEMS = gql`
  query GetAlertCardWorkItems($pagination: PaginationInput!, $filters: WorkItemFiltersInput) {
    getWorkItems(pagination: $pagination, filters: $filters) {
      edges {
        node {
          ...WorkItemDetails
        }
      }
    }
  }
  ${WorkItemFragment}
`;

/**
 * Resolve the work items a transcript's cards name, keyed by id — every
 * clickable alert id, migrated or fresh, resolves against the work-item store.
 *
 * Hoisted to the pane rather than fetched by the card itself so
 * `AssistantPaneView` stays prop-pure — its stories render from fixtures with
 * no Apollo client, and a self-fetching child would break every one of them.
 * One query for the whole transcript, not one per card.
 *
 * `archived` is deliberately not passed: a card outlives its alert's active
 * life — the turn that expanded it stays in the transcript after the item is
 * archived, and a header that blanked out on archive would strand the reply
 * under it. There is no priority ceiling or freshness window to override;
 * `ids` just narrows.
 *
 * An id that resolves to nothing (deleted with the transaction it hung off, or
 * scoped away) is simply absent from the map; the card renders its unavailable
 * state.
 *
 * @param messages - the transcript, scanned for the alerts its cards name.
 * @returns `cards`, the resolved rows keyed by id and projected through
 *   `workItemToListedAlert` so a card derives its header exactly the way the
 *   strip row does (ids that did not resolve are absent); and `workedAlertIds`,
 *   those the user has already seen or that have moved on — read straight off
 *   the work item, since the card projection is a rendering shape rather than
 *   the source of that fact.
 */
export function useAlertCardWorkItems(messages: IConversationMessage[]): IAlertCardWorkItems {
  const alertIds = React.useMemo(() => collectAlertCardIds(messages), [messages]);

  const { data, previousData } = useQuery<GetAlertCardWorkItemsQuery, GetAlertCardWorkItemsQueryVariables>(
    GET_ALERT_CARD_WORK_ITEMS,
    {
      variables: {
        pagination: { limit: alertIds.length, direction: PaginationDirection.Forward },
        filters: { ids: alertIds },
      },
      skip: alertIds.length === 0,
    }
  );

  return React.useMemo(() => {
    // `previousData` covers the window where a mutation evicted the
    // `getWorkItems` field and the refetch is in flight — without it every card
    // in the transcript would flash its unavailable state.
    const edges = (data ?? previousData)?.getWorkItems?.edges ?? [];

    return {
      cards: Object.fromEntries(edges.map((edge) => [edge.node.id, workItemToListedAlert(edge.node)])),
      workedAlertIds: new Set(edges.filter((edge) => !isWorkItemUnread(edge.node)).map((edge) => edge.node.id)),
    };
  }, [data, previousData]);
}
